import React from "react";
import { Box, Pagination, ThemeProvider, createTheme } from "@mui/material";
import colors from "../../themes/colors";
import { classNames } from "../../models/models";
import TablePageSizeSelect from "./TablePageSizeSelect";
import useTranslation from "../../../shared/hooks/useTranslation";

type TablePaginationProps = {
  pageNo?: number;
  pageSize?: number;
  totalCount?: number;
  variant?: "default" | "bottom";
  className?: string;
  onPageNoChange?: (pageNo: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
};

const TablePagination = ({
  pageNo = 1,
  pageSize = 1,
  totalCount = 1,
  variant = "default",
  className,
  onPageNoChange,
  onPageSizeChange
}: TablePaginationProps) => {
  const { translate: tCommon } = useTranslation("common");

  const PageSelect = () => {
    return (
      <Pagination
        variant="text"
        shape="rounded"
        count={totalPages || 1}
        page={pageNo}
        onChange={(event, pageNo) => handlePageNoChange(pageNo)}
      />
    );
  };

  const ResultsIndicator = () => {
    if (!totalCount) {
      return (<></>);
    }

    const firstItemNo = pageSize * (pageNo - 1) + 1;
    const lastItemNo = Math.min(pageSize * pageNo, totalCount);

    return (
      <p className="text-12-regular">
        {tCommon("common.results")} {firstItemNo}-{lastItemNo} {tCommon("common.of")} {totalCount}
      </p>
    );
  };

  const handlePageNoChange = (pageNo: number) => {
    if (onPageNoChange) {
      onPageNoChange(pageNo);
    }
  };

  const handlePageSizeChange = (pageSize: number) => {
    if (onPageSizeChange) {
      onPageSizeChange(pageSize);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const theme = createTheme({});

  const bottomContainerStyle = {
    borderTop: `1px solid ${colors.secondary.gray80}`,
    backgroundColor: colors.primary.white1,
    padding: "0.75rem 1rem"
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        className={classNames("flex justify-between items-center", className)}
        sx={variant === "bottom" ? bottomContainerStyle : {}}>
        <PageSelect />

        <Box>
          <ResultsIndicator />
        </Box>

        <Box>
          <TablePageSizeSelect value={pageSize} onChange={handlePageSizeChange} />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default TablePagination;